import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  IconButton, Box, Typography, CircularProgress, Avatar, Chip,
  Tooltip, Link as MuiLink, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { getReviewDetail, likeReview } from '../../services/api';
import { ReviewDetail } from '../../types/Review';
import { formatReviewText } from '../../utils/formatText';
import { scoreTags } from '../../utils/scoreTags';
import { getPlatformDisplayText } from '../../utils/PlatformList';
import DetailScores from '../review/DetailScores';
import ShareButtons from '../common/ShareButtons';

const getScoreColor = (score: number | null) => {
  if (score === null) return '#666666';
  if (score >= 4.5) return '#4CAF50';
  if (score >= 4.0) return '#8BC34A';
  if (score >= 3.5) return '#CDDC39';
  if (score >= 3.0) return '#FFEB3B';
  if (score >= 2.5) return '#FFC107';
  if (score >= 2.1) return '#FF9800';
  return '#F44336';
};

const getReviewExcerpt = (text: string, maxLength = 60) => {
  if (!text) return '';
  const trimmed = text.trim().replace(/\s+/g, ' ');
  return trimmed.length > maxLength ? `${trimmed.slice(0, maxLength)}...` : trimmed;
};

const formatDate = (date: string) =>
  new Date(date).toLocaleString('ja-JP', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

interface GameReviewPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  gameId: number;
  reviewId: number;
  /** own profile のみ渡す。存在する場合のみ編集ボタンを表示 */
  onRequestEdit?: () => void;
}

const GameReviewPreviewDialog: React.FC<GameReviewPreviewDialogProps> = ({
  open, onClose, gameId, reviewId, onRequestEdit,
}) => {
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [userLikeCount, setUserLikeCount] = useState(0);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const fetchReview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getReviewDetail(gameId, reviewId);
      setReview(data);
      setIsLiked(data.is_liked);
      setLikeCount(data.like_count);
      setUserLikeCount(data.user_like_count || 0);
    } catch (e) {
      console.error('レビューの取得に失敗しました', e);
      setError('レビューの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [gameId, reviewId]);

  useEffect(() => {
    if (open) fetchReview();
  }, [open, fetchReview]);

  const gameName = review ? (review.game.japanese_name || review.game.name) : '';

  const handleLikeClick = async () => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    if (userLikeCount >= 10) return;
    try {
      const response = await likeReview(reviewId);
      setIsLiked(true);
      setLikeCount(response.data.count);
      setUserLikeCount(response.data.like_count);
    } catch (error: any) {
      console.error('いいね処理に失敗しました', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: { xs: 0, md: '12px' },
        },
      }}
    >
      <DialogTitle sx={{ py: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {gameName ? `${gameName} のレビュー` : 'レビュー'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Typography variant="body2" sx={{ color: 'error.main', textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && review && (
          <>
            {/* ユーザー情報 */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                src={review.avatar || undefined}
                alt={review.username}
                sx={{ width: 40, height: 40 }}
              />
              <Box sx={{ ml: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <MuiLink
                    component={RouterLink}
                    to={`/profile/${review.username}`}
                    variant="body2"
                    onClick={onClose}
                    sx={{
                      textDecoration: 'none',
                      color: 'text.primary',
                      fontWeight: 600,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {review.display_name || review.username}
                  </MuiLink>
                  {review.status && review.status !== 1 && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        bgcolor: review.status === 2 ? '#2196F320' : '#FF980020',
                        color: 'rgba(255, 255, 255, 0.788)',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.6rem',
                        lineHeight: 1,
                        border: `1px solid ${review.status === 2 ? '#2196F340' : '#FF980040'}`,
                      }}
                    >
                      {review.status === 2 ? 'プレイ中' : review.status === 3 ? '中断' : null}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    投稿日：{formatDate(review.created_at)}
                  </Typography>
                  {review.updated_at && review.updated_at !== review.created_at && (
                    <Tooltip title={`更新日：${formatDate(review.updated_at)}`}>
                      <UpdateIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* スコア + スコアタグ */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 1.5,
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              {review.score !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '140px' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.8rem',
                      py: 0.5,
                      px: 1,
                      lineHeight: 1,
                      borderRadius: 1,
                      backgroundColor: `${getScoreColor(review.score)}B3`,
                    }}
                  >
                    {review.score.toFixed(1)}
                  </Typography>
                  <Box
                    sx={{
                      width: 100,
                      height: 4,
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${((review.score - 1) / 4) * 100}%`,
                        height: '100%',
                        bgcolor: getScoreColor(review.score),
                        borderRadius: 2,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1 }}>
                {review.score_tags?.map((tagId: number) => {
                  const tag = scoreTags.find((t) => t.id === tagId);
                  return tag && (
                    <Chip
                      key={tag.id}
                      icon={
                        tag.id === 9 || tag.id >= 30 ? (
                          <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                        ) : (
                          <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                        )
                      }
                      label={tag.label}
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiChip-icon': { fontSize: '0.7rem' },
                        '& .MuiChip-label': { fontSize: '0.6rem' },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* 詳細スコア */}
            <Box sx={{ mb: 1.5 }}>
              <DetailScores
                scoreVisual={review.score_visual}
                scoreSound={review.score_sound}
                scoreGameplay={review.score_gameplay}
                scoreNarrative={review.score_narrative}
                scoreOriginality={review.score_originality}
              />
            </Box>

            {/* プレイ情報 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 1, columnGap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TimerOutlinedIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'pulse.info' }} />
                <Typography variant="body2" color="pulse.info" sx={{ lineHeight: 1 }}>
                  {review.play_time ? `${review.play_time}時間` : '-'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PlayCircleOutlineIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'pulse.info' }} />
                <Typography variant="body2" color="pulse.info" sx={{ lineHeight: 1 }}>
                  {review.play_start_date || '-'} - {review.play_end_date || '-'}
                </Typography>
              </Box>
              {review.platform && review.platform.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SportsEsportsOutlinedIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'pulse.info' }} />
                  <Typography variant="body2" color="pulse.info" sx={{ lineHeight: 1 }}>
                    {getPlatformDisplayText(review.platform)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* レビュー本文 */}
            <Box
              sx={{
                color: 'text.primary',
                '& .spoiler': {
                  backgroundColor: '#444',
                  padding: '0 4px',
                  borderRadius: '2px',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'transparent' },
                },
              }}
            >
              <Typography variant="body2" color="pulse.text" sx={{ lineHeight: { xs: 1.35, sm: 1.4 } }}>
                {formatReviewText(review.review_text, isMobile)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          pb: fullScreen ? 'calc(8px + env(safe-area-inset-bottom))' : 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* 左: いいね・詳細リンク・共有 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            onClick={handleLikeClick}
            startIcon={isLiked ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            color={isLiked ? 'primary' : 'inherit'}
            disabled={userLikeCount >= 10}
            size="small"
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.25,
              fontSize: '0.75rem',
              borderRadius: 1,
              lineHeight: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
              '& .MuiButton-startIcon': { mr: 1, '& svg': { fontSize: '0.8rem' } },
              '&.Mui-disabled': { opacity: 0.7, color: theme.palette.primary.main },
            }}
          >
            <Typography variant="caption" color="text.primary" lineHeight={1}>
              {likeCount}
              {userLikeCount > 0 && (
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                  {` (+${userLikeCount})`}
                </Typography>
              )}
            </Typography>
          </Button>
          {!loading && !error && review && (
            <ShareButtons
              text={`『${gameName}』のレビュー by ${review.display_name || review.username}\n\n${getReviewExcerpt(review.review_text)}`}
              url={`/share/review/${gameId}/${reviewId}`}
              hashtags={['PULSE_DB']}
              buttonText="共有"
            />
          )}
          {/* <Tooltip title="詳細ページを開く" arrow>
            <MuiLink
              component={RouterLink}
              to={`/game/${gameId}/reviews/${reviewId}`}
              onClick={onClose}
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <OpenInNewIcon sx={{ fontSize: '1rem' }} />
            </MuiLink>
          </Tooltip> */}
        </Box>

        {/* 右: 閉じる・編集 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button onClick={onClose} color="inherit" size="small">
            閉じる
          </Button>
          {onRequestEdit && (
            <Button
              onClick={onRequestEdit}
              variant="contained"
              size="small"
              disabled={loading || !!error}
              startIcon={<EditIcon sx={{ fontSize: '0.9rem !important' }} />}
            >
              編集
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default GameReviewPreviewDialog;
